import React, { useContext, useEffect, useState } from "react";
import {
    Loader2,
    ChevronLeft,
    Check,
    ArrowRight,
    RotateCcw,
    MessageCircle,
    Mail,
    Smartphone,
    Phone,
    Eye,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { callComConfigApi } from "./callComConfigApi";
import AuthContext  from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./ComConfigEventPolicy.module.css";

// authCtx?.authIdentifier is assumed to be the authenticated user's identifier
// for createdBy/authIdentifier — adjust to whatever the real AuthContext
// actually exposes.

const CHANNEL_ICONS = {
    WHATSAPP: MessageCircle,
    SMS: Smartphone,
    EMAIL: Mail,
    VOICE: Phone,
};

const ComConfigEventPolicy = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const { activeStore: activeStoreJson } = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return activeStoreJson ? JSON.parse(activeStoreJson) : null; } catch { return null; } })();

    const [event, setEvent] = useState(null);
    const [resolvedPolicy, setResolvedPolicy] = useState(null);
    const [strategies, setStrategies] = useState([]);
    const [selectedStrategyVersionId, setSelectedStrategyVersionId] = useState(null);
    const [previewStrategy, setPreviewStrategy] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadEventPolicy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, activeStore?.storeIdentifier]);

    const loadEventPolicy = async () => {
        if (!eventId || !activeStore?.storeIdentifier || !authCtx?.token) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setMessage(null);

        const [eventRes, policyRes, strategiesRes] = await Promise.all([
            callComConfigApi("comConfigGetEvents", { eventId }, authCtx),
            callComConfigApi(
                "comConfigGetResolvedPolicy",
                { storeIdentifier: activeStore.storeIdentifier, eventId },
                authCtx
            ),
            callComConfigApi(
                "comConfigGetStrategies",
                { storeIdentifier: activeStore.storeIdentifier },
                authCtx
            ),
        ]);

        if (eventRes?.status === "success") setEvent(eventRes.event || null);
        if (policyRes?.status === "success") {
            const policy = policyRes.policy || null;
            setResolvedPolicy(policy);
            setSelectedStrategyVersionId(policy?.resolvedStrategyVersionId || null);
        }
        if (strategiesRes?.status === "success") {
            setStrategies(Array.isArray(strategiesRes.strategies) ? strategiesRes.strategies : []);
        }

        setIsLoading(false);
    };

    const handlePreviewStrategy = async (strategyVersionId) => {
        const res = await callComConfigApi("comConfigGetStrategyVersionDetail", { strategyVersionId }, authCtx);
        if (res?.status === "success") {
            setPreviewStrategy(res.response);
        }
    };

    const handleSelectStrategy = (strategyVersionId) => {
        setSelectedStrategyVersionId(strategyVersionId);
        handlePreviewStrategy(strategyVersionId);
    };

    const handleSave = async () => {
        if (!selectedStrategyVersionId) return;

        setIsSaving(true);
        const res = await callComConfigApi(
            "comConfigSavePolicyOverride",
            {
                storeIdentifier: activeStore?.storeIdentifier,
                eventId,
                strategyVersionId: selectedStrategyVersionId,
                authIdentifier: authCtx?.authIdentifier,
            },
            authCtx
        );

        if (res?.status === "success") {
            setMessage({ type: "success", text: "Saved." });
            loadEventPolicy();
        } else {
            setMessage({ type: "error", text: res?.message || "Couldn't save. Try again." });
        }
        setIsSaving(false);
    };

    const handleRevert = async () => {
        setIsSaving(true);
        const res = await callComConfigApi(
            "comConfigRevertPolicyOverride",
            { storeIdentifier: activeStore?.storeIdentifier, eventId },
            authCtx
        );

        if (res?.status === "success") {
            setMessage({ type: "success", text: "Back to platform default." });
            loadEventPolicy();
        } else {
            setMessage({ type: "error", text: res?.message || "Couldn't revert. Try again." });
        }
        setIsSaving(false);
    };

    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader2 className={styles.spinner} size={22} />
                <span>Loading event…</span>
            </div>
        );
    }

    if (!event) {
        return (
            <div className={styles.stateContainer}>
                <span>Event not found.</span>
            </div>
        );
    }

    const isCustomized = resolvedPolicy?.source === "STORE_OVERRIDE";

    return (
        <div className={styles.container}>
            <button className={styles.backLink} onClick={() => navigate("/communication-settings")}>
                <ChevronLeft size={16} />
                Back to communication settings
            </button>

            <div className={styles.header}>
                <h1 className={styles.title}>{event.eventName}</h1>
                <p className={styles.eventCode}>{event.eventCode}</p>
                {event.description && <p className={styles.description}>{event.description}</p>}
                {event.isMandatory ? (
                    <span className={styles.mandatoryNote}>
                        This is a critical event — it can't be turned off, only its channel order adjusted.
                    </span>
                ) : null}
            </div>

            <div className={styles.statusRow}>
                <span className={isCustomized ? styles.statusCustomized : styles.statusDefault}>
                    {isCustomized ? "Using a custom strategy" : "Using the platform default"}
                </span>
                {isCustomized && (
                    <button className={styles.revertButton} onClick={handleRevert} disabled={isSaving}>
                        <RotateCcw size={13} />
                        Revert to default
                    </button>
                )}
                <button className={styles.previewLink} onClick={() => navigate(`/communication-settings/${eventId}/preview/CUSTOMER`)}>
                    <Eye size={13} />
                    Preview what gets sent
                </button>
            </div>

            <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Choose a strategy</h2>
                <p className={styles.sectionHint}>
                    A strategy decides which channels are tried, in what order, for this event.
                </p>

                <div className={styles.strategyGrid}>
                    {strategies.map((strategy) => {
                        const isSelected = strategy.strategyVersionId === selectedStrategyVersionId;
                        return (
                            <button
                                key={strategy.strategyVersionId}
                                className={isSelected ? styles.strategyCardSelected : styles.strategyCard}
                                onClick={() => handleSelectStrategy(strategy.strategyVersionId)}
                            >
                                <div className={styles.strategyCardHeader}>
                                    <span className={styles.strategyName}>{strategy.strategyName}</span>
                                    {isSelected && <Check size={16} className={styles.checkIcon} />}
                                </div>
                                <p className={styles.strategyDescription}>{strategy.description}</p>
                                <span className={styles.strategyOwner}>
                                    {strategy.strategyOwner === "PLATFORM" ? "Platform" : "Your own"}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {previewStrategy && (
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Cascade preview</h2>
                    <div className={styles.stepFlow}>
                        {previewStrategy.steps?.map((step, index) => {
                            const ChannelIcon = CHANNEL_ICONS[step.channelCode] || MessageCircle;
                            return (
                                <React.Fragment key={step.ID}>
                                    <div className={styles.stepCard}>
                                        <ChannelIcon size={16} />
                                        <span>{step.channelName}</span>
                                        {step.conditionJson && (
                                            <span className={styles.conditionNote}>conditional</span>
                                        )}
                                    </div>
                                    {index < previewStrategy.steps.length - 1 && (
                                        <ArrowRight size={14} className={styles.stepArrow} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}

            {message && (
                <div className={message.type === "success" ? styles.messageSuccess : styles.messageError}>
                    {message.text}
                </div>
            )}

            <div className={styles.actions}>
                <button
                    className={styles.saveButton}
                    onClick={handleSave}
                    disabled={isSaving || !selectedStrategyVersionId}
                >
                    {isSaving ? <Loader2 size={15} className={styles.spinnerInline} /> : null}
                    Save changes
                </button>
            </div>
        </div>
    );
};

export default ComConfigEventPolicy;